/* Futonhaven original UI. Uses Shopify's native storefront forms and Cart API. */
(() => {
  'use strict';
  const cfg = window.Futonhaven || {};
  const root = cfg.routes?.root || '/';
  const $ = (selector, parent = document) => parent.querySelector(selector);
  const $$ = (selector, parent = document) => Array.from(parent.querySelectorAll(selector));
  const money = value => new Intl.NumberFormat(cfg.locale || 'de-DE', {
    style: 'currency', currency: cfg.currency || 'EUR'
  }).format(Number(value || 0) / 100);
  const text = value => String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
  const endpoint = suffix => `${root}${suffix}`;

  // Floating hero navigation / keyboard accessible menu and search.
  const header = $('[data-site-header]');
  if (header) {
    const menu = $('[data-mobile-nav]', header);
    const search = $('[data-search-panel]', header);
    const menuToggle = $('[data-menu-toggle]', header);
    const searchToggle = $('[data-search-toggle]', header);
    const closeMenus = () => {
      if (menu) menu.hidden = true;
      if (search) search.hidden = true;
      menuToggle?.setAttribute('aria-expanded', 'false');
      searchToggle?.setAttribute('aria-expanded', 'false');
    };
    const updateHeader = () => {
      header.classList.toggle('is-scrolled', Boolean(window.scrollY > 12 && header.dataset.sticky === 'true'));
    };
    updateHeader();
    window.addEventListener('scroll', updateHeader, {passive: true});
    menuToggle?.addEventListener('click', () => {
      const open = Boolean(menu?.hidden);
      closeMenus();
      if (menu) menu.hidden = !open;
      menuToggle.setAttribute('aria-expanded', String(open));
      if (open) $('a', menu)?.focus();
    });
    searchToggle?.setAttribute('aria-expanded', 'false');
    searchToggle?.addEventListener('click', () => {
      const open = Boolean(search?.hidden);
      closeMenus();
      if (search) search.hidden = !open;
      searchToggle.setAttribute('aria-expanded', String(open));
      if (open) $('input[type="search"]', search)?.focus();
    });
    document.addEventListener('click', event => {
      if (!header.contains(event.target)) closeMenus();
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') closeMenus();
    });
  }

  // Native Shopify Cart API: add, remove and change item quantity.
  const drawer = $('#CartDrawer');
  const overlay = $('[data-drawer-overlay]');
  const drawerContent = $('[data-cart-drawer-content]');
  let previousFocus;
  let drawerRequest = 0;
  function drawerClose() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.hidden = true;
    document.body.classList.remove('no-scroll');
    previousFocus?.focus?.();
  }
  function drawerOpen() {
    if (!drawer) return;
    previousFocus = document.activeElement;
    if (overlay) overlay.hidden = false;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    drawer.focus();
    refreshCart();
  }
  $$('[data-cart-open]').forEach(button => button.addEventListener('click', drawerOpen));
  $('[data-cart-close]')?.addEventListener('click', drawerClose);
  overlay?.addEventListener('click', drawerClose);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && drawer?.classList.contains('is-open')) drawerClose();
    if (event.key !== 'Tab' || !drawer?.classList.contains('is-open')) return;
    const focusables = $$('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled])', drawer);
    if (!focusables.length) return;
    const first = focusables[0], last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  async function requestJSON(path, options = {}) {
    const response = await fetch(endpoint(path), {credentials: 'same-origin', ...options});
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Die Anfrage ist fehlgeschlagen.');
    return data;
  }
  async function refreshCart() {
    const currentRequest = ++drawerRequest;
    try {
      const cart = await requestJSON('cart.js');
      if (currentRequest !== drawerRequest) return;
      $$('[data-cart-count]').forEach(node => { node.textContent = cart.item_count; });
      if (!drawerContent) return;
      if (!cart.item_count) {
        drawerContent.innerHTML = `<div class="empty-drawer"><p>Ihr Warenkorb ist noch leer.</p><a class="pill-btn pill-btn--dark" href="${text(cfg.routes?.root || '/') }collections/all">Möbel entdecken ↗</a></div>`;
        return;
      }
      drawerContent.innerHTML = cart.items.map((item, i) => `
        <div class="drawer-cart-item">
          ${item.image ? `<img src="${text(item.image)}" alt="${text(item.product_title)}" loading="lazy">` : '<div class="drawer-cart-image-empty"></div>'}
          <div class="drawer-cart-detail">
            <a href="${text(item.url)}"><strong>${text(item.product_title)}</strong></a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<small>${text(item.variant_title)}</small>` : ''}
            <small>${money(item.final_line_price)}</small>
            <div class="drawer-qty" aria-label="Anzahl ändern">
              <button type="button" data-line-change="${i + 1}" data-new-qty="${item.quantity - 1}" aria-label="Menge verringern">−</button>
              <span aria-label="Menge">${item.quantity}</span>
              <button type="button" data-line-change="${i + 1}" data-new-qty="${item.quantity + 1}" aria-label="Menge erhöhen">+</button>
            </div>
            <button type="button" class="drawer-remove" data-line-change="${i + 1}" data-new-qty="0">Entfernen</button>
          </div>
        </div>`).join('') + `<div class="drawer-total"><span>Zwischensumme</span><span>${money(cart.total_price)}</span></div>
        <p class="drawer-tax">Steuern und Versandkosten nach den geltenden Einstellungen.</p>`;
    } catch (error) {
      if (drawerContent && currentRequest === drawerRequest) drawerContent.textContent = 'Der Warenkorb konnte nicht geladen werden.';
    }
  }
  drawer?.addEventListener('click', async event => {
    const control = event.target.closest('[data-line-change]');
    if (!control || control.disabled) return;
    control.disabled = true;
    try {
      await requestJSON('cart/change.js', {method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({line: Number(control.dataset.lineChange), quantity: Number(control.dataset.newQty)})});
      await refreshCart();
    } catch (error) {
      control.disabled = false;
      const notice = document.createElement('p');
      notice.className = 'drawer-error'; notice.role = 'alert'; notice.textContent = error.message;
      drawerContent?.prepend(notice);
    }
  });

  // Native <form 'product'> keeps the hidden/selected variant ID and checkout integration.
  document.addEventListener('submit', async event => {
    const form = event.target.closest('form[data-ajax-add],form.js-product-form');
    if (!form) return;
    // Shopify-rendered accelerated checkout buttons should submit natively.
    if (form.matches('.js-product-form') && event.submitter?.name !== 'add') return;
    event.preventDefault();
    const button = event.submitter || $('button[name="add"]', form);
    if (button) button.disabled = true;
    const feedback = $('[data-product-feedback]', form);
    if (feedback) { feedback.textContent = ''; feedback.classList.remove('error'); }
    try {
      await requestJSON('cart/add.js', {method: 'POST', body: new FormData(form)});
      drawerOpen();
      if (feedback) feedback.textContent = 'Zum Warenkorb hinzugefügt.';
    } catch (error) {
      if (feedback) { feedback.textContent = error.message; feedback.classList.add('error'); }
      else window.alert(error.message);
    } finally {
      if (button) {
        const root = form.closest('[data-product-root]');
        const selector = root && $('[data-variant-select]', root);
        const data = root && $('[data-product-variants]', root);
        let active = true;
        try { active = JSON.parse(data?.textContent || '[]').find(v => String(v.id) === String(selector?.value))?.available !== false; } catch (_) { }
        button.disabled = !active;
      }
    }
  });

  // Variant state: availability, verified stock, price, SKU, selected image and URL.
  $$('[data-product-root]').forEach(box => {
    const selector = $('[data-variant-select]', box);
    const dataNode = $('[data-product-variants]', box);
    const price = $('[data-current-price]', box);
    const inventory = $('[data-stock-status]', box);
    const sku = $('[data-product-sku]', box);
    const buy = $('[data-add-button]', box);
    const photo = $('.product-main-image', box);
    const accelerated = $('[data-dynamic-checkout]', box);
    const quantity = $('input[name="quantity"]', box);
    const lightbox = $('[data-product-lightbox]', box);
    let variants = [];
    try { variants = JSON.parse(dataNode?.textContent || '[]'); } catch (_) { }
    function changeVariant(variantId, updateURL = true) {
      const variant = variants.find(item => String(item.id) === String(variantId));
      if (!variant) return;
      const tracked = Boolean(variant.inventoryManagement);
      const finite = tracked && variant.inventoryPolicy === 'deny';
      const remaining = Number(variant.inventoryQuantity);
      let label = 'Ausverkauft';
      if (variant.available) {
        if (finite) label = `${Math.max(0, remaining)} auf Lager`;
        else if (tracked) label = 'Bestellbar; Lagerbestand nicht bestätigt';
        else label = 'Bestellbar (Bestand nicht ausgewiesen)';
      }
      if (price) price.innerHTML = money(variant.price) +
        (variant.compareAtPrice > variant.price ? ` <s>${money(variant.compareAtPrice)}</s>` : '');
      if (inventory) inventory.innerHTML = `<span class="stock-dot ${variant.available ? '' : 'stock-dot--out'}"></span>${text(label)}`;
      if (sku) sku.textContent = variant.sku ? `Art.-Nr. ${variant.sku}` : '';
      if (buy) {buy.disabled = !variant.available; buy.textContent = variant.available ? 'In den Warenkorb ↗' : 'Ausverkauft';}
      if (accelerated) accelerated.hidden = !variant.available;
      if (quantity) {
        if (finite && variant.available && remaining > 0) quantity.max = String(remaining);
        else quantity.removeAttribute('max');
        quantity.value = String(Math.max(1, Math.min(Number(quantity.value) || 1, Number(quantity.max) || Infinity)));
      }
      if (variant.image && photo) {
        photo.src = variant.image;
        photo.srcset = '';
        $$('.thumbnail-button', box).forEach(button => {
          button.classList.toggle('is-active', button.dataset.fullImage?.split('?')[0] === variant.image.split('?')[0]);
        });
      }
      if (updateURL) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({variant: variant.id}, '', url);
      }
    }
    selector?.addEventListener('change', () => changeVariant(selector.value));
    const clampQty = value => Math.max(1, Math.min(Math.floor(Number(value) || 1), Number(quantity?.max) || Infinity));
    $('[data-qty-minus]', box)?.addEventListener('click', () => { if (quantity) quantity.value = String(clampQty(Number(quantity.value) - 1)); });
    $('[data-qty-plus]', box)?.addEventListener('click', () => { if (quantity) quantity.value = String(clampQty(Number(quantity.value) + 1)); });
    quantity?.addEventListener('change', () => { quantity.value = String(clampQty(quantity.value)); });
    $$('[data-thumbnail]', box).forEach(button => button.addEventListener('click', () => {
      if (!photo) return;
      photo.src = button.dataset.fullImage || photo.src;
      photo.srcset = '';
      photo.alt = button.dataset.imageAlt || '';
      $$('[data-thumbnail]', box).forEach(element => element.classList.toggle('is-active', element === button));
    }));
    photo?.addEventListener('click', () => {
      if (!lightbox || typeof lightbox.showModal !== 'function') return;
      const image = $('[data-lightbox-image]', lightbox);
      if (image) { image.src = photo.currentSrc || photo.src; image.alt = photo.alt; }
      lightbox.showModal();
    });
    $('[data-lightbox-close]', box)?.addEventListener('click', () => lightbox?.close());
    lightbox?.addEventListener('click', event => { if (event.target === lightbox) lightbox.close(); });
    if (selector?.value) changeVariant(selector.value, false);
  });
})();
